import React from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailIcon from "@mui/icons-material/Mail";
import logoMenu from "../static/media/logoGRMPNG.webp";

const C = {
  darkBlue: "#0C1F2F",
  navy: "#0057A8",
  gold: "#84945c",
  goldDark: "#6b7a48",
  softGold: "#c2ed80",
  white: "#ffffff",
};

const SECTIONS = [
  { path: "/", label: "Inicio" },
  { path: "/tienda", label: "Tienda" },
  { path: "/nosotros", label: "Nosotros" },
  { path: "/contacto", label: "Contacto" },
];

const LEGAL_SECTIONS = [
  { path: "/terminos", label: "Términos y Condiciones" },
  { path: "/privacidad", label: "Política de Privacidad" },
];

// Arreglo con ambos números y sus enlaces a WhatsApp
const PHONES = [
  {
    display: "+593 99 994 3920",
    url: "https://api.whatsapp.com/send/?phone=593999943920&text=Hola+quiero+m%C3%A1s+informaci%C3%B3n&type=phone_number&app_absent=0",
  },
  {
    display: "+593 98 772 4998",
    url: "https://api.whatsapp.com/send/?phone=593987724998&text=Hola+quiero+m%C3%A1s+informaci%C3%B3n&type=phone_number&app_absent=0",
  },
];

// Mantenemos el primer número como el principal para el ícono social superior
const WHATSAPP_URL_MAIN = PHONES[0].url;

const INSTAGRAM_URL = "https://www.instagram.com/electrica_grm/";
const FACEBOOK_URL = "https://www.facebook.com/profile.php?id=100078693296652";
const DEVZONE_URL = "https://devzone-ec.com/";
const PRINDELA_URL = "https://www.prindela.com/";

const EMAILS = ["ventasce@electricagrm.com"];

function SocialButton({ href, label, children }) {
  return (
    <Box
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      sx={{
        width: 38,
        height: 38,
        borderRadius: "50%",
        border: `1.5px solid rgba(12, 31, 47, 0.15)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: C.darkBlue,
        background: "transparent",
        transition: "all .2s ease",
        flexShrink: 0,
        "&:hover": {
          background: C.darkBlue,
          color: C.softGold,
          borderColor: C.darkBlue,
          transform: "translateY(-3px)",
          boxShadow: "0 6px 15px rgba(12, 31, 47, 0.15)",
        },
      }}
    >
      {children}
    </Box>
  );
}

function FooterHeading({ children }) {
  return (
    <Typography
      sx={{
        color: C.darkBlue,
        fontWeight: 700, // Títulos en negrilla
        fontSize: "12px",
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        mb: "16px",
      }}
    >
      {children}
    </Typography>
  );
}

export default function Footer() {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  const openInNewTab = (path) => {
    window.open(path, "_blank", "noopener,noreferrer");
  };

  return (
    <Box
      component="footer"
      sx={{ width: "100%", display: "flex", flexDirection: "column" }}
    >
      {/* LÍNEA DE TRANSICIÓN SUPERIOR */}
      <Box
        sx={{
          height: "4px",
          width: "100%",
          background: `linear-gradient(90deg, ${C.navy} 0%, ${C.gold} 100%)`,
          zIndex: 2,
        }}
      />

      {/* SECCIÓN SUPERIOR CON DEGRADADO SUTIL */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${C.softGold} 0%, #cbf291 50%, ${C.softGold} 100%)`,
          width: "100%",
          pt: { xs: 5, md: 6 },
          pb: { xs: 4, md: 5 },
        }}
      >
        <Box
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: "24px", sm: "40px", md: "56px" },
            boxSizing: "border-box",
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1.5fr 1fr 1fr 1.2fr",
              },
              gap: { xs: "32px", sm: "24px", md: "30px" },
            }}
          >
            {/* COLUMNA 1: MARCA */}
            <Box>
              <Box
                onClick={() => handleNavigation("/")}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  cursor: "pointer",
                  mb: "16px",
                  width: "fit-content",
                }}
              >
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "50%",
                    border: `2px solid ${C.darkBlue}`,
                    background: C.white,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: `0 0 0 3px rgba(12, 31, 47, 0.05)`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={logoMenu}
                    alt="Eléctrica GRM"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                    sx={{
                      width: 32,
                      height: 32,
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    lineHeight: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: C.darkBlue,
                      fontWeight: 750, // Logo en negrilla
                      fontSize: "18px",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                    }}
                  >
                    Eléctrica
                  </Typography>
                  <Typography
                    sx={{
                      color: C.darkBlue,
                      fontWeight: 700, // Logo en negrilla
                      fontSize: "10px",
                      letterSpacing: "0.24em",
                      textTransform: "uppercase",
                      mt: "3px",
                      opacity: 0.7,
                    }}
                  >
                    GRM
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  color: "rgba(12, 31, 47, 0.8)",
                  fontSize: "14px",
                  lineHeight: 1.6,
                  fontWeight: 400, // Sin negrilla
                  maxWidth: 320,
                  mb: "20px",
                }}
              >
                Material eléctrico, herramientas e iluminación con garantía y
                soporte directo de fábrica para todos tus proyectos.
              </Typography>

              <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                <SocialButton href={INSTAGRAM_URL} label="Instagram">
                  <InstagramIcon sx={{ fontSize: 18 }} />
                </SocialButton>
                <SocialButton href={FACEBOOK_URL} label="Facebook">
                  <FacebookIcon sx={{ fontSize: 18 }} />
                </SocialButton>
                <SocialButton href={WHATSAPP_URL_MAIN} label="WhatsApp">
                  <WhatsAppIcon sx={{ fontSize: 18 }} />
                </SocialButton>
              </Box>
            </Box>

            {/* COLUMNA 2: NAVEGACIÓN */}
            <Box>
              <FooterHeading>Navegación</FooterHeading>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                {SECTIONS.map((s) => (
                  <Typography
                    key={s.path}
                    component="button"
                    onClick={() => handleNavigation(s.path)}
                    sx={{
                      all: "unset",
                      display: "block",
                      cursor: "pointer",
                      color: "rgba(12, 31, 47, 0.8)",
                      fontSize: "14px",
                      fontWeight: 400, // Sin negrilla
                      transition: "color .2s, transform .2s",
                      "&:hover": {
                        color: C.darkBlue,
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    {s.label}
                  </Typography>
                ))}
              </Box>
            </Box>

            {/* COLUMNA 3: LEGAL */}
            <Box>
              <FooterHeading>Legal</FooterHeading>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "12px" }}
              >
                {LEGAL_SECTIONS.map((s) => (
                  <Typography
                    key={s.path}
                    component="button"
                    onClick={() => openInNewTab(s.path)}
                    sx={{
                      all: "unset",
                      display: "block",
                      cursor: "pointer",
                      color: "rgba(12, 31, 47, 0.8)",
                      fontSize: "14px",
                      fontWeight: 400, // Sin negrilla
                      transition: "color .2s, transform .2s",
                      "&:hover": {
                        color: C.darkBlue,
                        transform: "translateX(4px)",
                      },
                    }}
                  >
                    {s.label}
                  </Typography>
                ))}
              </Box>
            </Box>

            {/* COLUMNA 4: CONTACTO */}
            <Box>
              <FooterHeading>Contacto</FooterHeading>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  color: "rgba(12, 31, 47, 0.8)",
                  fontSize: "14px",
                  fontWeight: 400, // Sin negrilla
                  mb: "16px",
                }}
              >
                <Box
                  sx={{
                    background: "rgba(12, 31, 47, 0.08)",
                    p: "6px",
                    borderRadius: "50%",
                    display: "flex",
                  }}
                >
                  <LocationOnIcon sx={{ fontSize: 16, color: C.darkBlue }} />
                </Box>
                Quito, Ecuador
              </Box>

              {PHONES.map((phone) => (
                <Box
                  key={phone.display}
                  component="a"
                  href={phone.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    textDecoration: "none",
                    color: "rgba(12, 31, 47, 0.85)",
                    fontSize: "14px",
                    fontWeight: 400, // Sin negrilla
                    mb: "16px",
                    transition: "color .2s",
                    "&:hover": { color: C.navy },
                  }}
                >
                  <Box
                    sx={{
                      background: "rgba(12, 31, 47, 0.08)",
                      p: "6px",
                      borderRadius: "50%",
                      display: "flex",
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 16, color: C.darkBlue }} />
                  </Box>
                  {phone.display}
                </Box>
              ))}

              {EMAILS.map((email) => (
                <Box
                  key={email}
                  component="a"
                  href={`mailto:${email}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    textDecoration: "none",
                    color: "rgba(12, 31, 47, 0.85)",
                    fontSize: "14px",
                    fontWeight: 400, // Sin negrilla
                    mb: "12px",
                    transition: "color .2s",
                    "&:hover": { color: C.navy },
                  }}
                >
                  <Box
                    sx={{
                      background: "rgba(12, 31, 47, 0.08)",
                      p: "6px",
                      borderRadius: "50%",
                      display: "flex",
                    }}
                  >
                    <MailIcon sx={{ fontSize: 16, color: C.darkBlue }} />
                  </Box>
                  {email}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* SECCIÓN INFERIOR */}
      <Box sx={{ background: C.gold, width: "100%", py: 2 }}>
        <Box
          sx={{
            maxWidth: "1280px",
            mx: "auto",
            px: { xs: "24px", sm: "40px", md: "56px" },
            boxSizing: "border-box",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "center", sm: "center" },
            gap: "8px",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: 400, // Sin negrilla
            }}
          >
            © 2026 ELÉCTRICA GRM — Ecuador, Quito
          </Typography>
          <Typography
            sx={{
              color: "#ffffff",
              fontSize: "12px",
              fontWeight: 400, // Sin negrilla
            }}
          >
            Desarrollado por{" "}
            <Box
              component="a"
              href={DEVZONE_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: C.white,
                fontWeight: 400, // Sin negrilla
                textDecoration: "none",
                transition: "color .2s",
                "&:hover": { color: C.white },
              }}
            >
              DevZone
            </Box>{" "}
            &{" "}
            <Box
              component="a"
              href={PRINDELA_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: C.white,
                fontWeight: 400, // Sin negrilla
                textDecoration: "none",
                transition: "color .2s",
                "&:hover": { color: C.white },
              }}
            >
              Prindela Service S.A.S
            </Box>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
